import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrder, addDelivery, updateDelivery, deleteDelivery, exportBuyerPDF, exportSupplierPDF, getTransporters, validateBales, getOrderAuditLogs } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatDate, getStatusColor } from '../lib/utils';
import { ArrowLeft, Edit, Plus, Package, Pencil, Trash2, User, Building2, AlertTriangle, History, Keyboard, Download, CheckCircle2, Clock, XCircle, CircleDot } from 'lucide-react';
import ExportDialog from '../components/ExportDialog';
import useExportShortcut from '../hooks/useExportShortcut';
import { exportOrderDetailToExcel, exportOrderDetailToPDF } from '../utils/exportUtils';
import { MasterDataAutocomplete } from '../components/MasterDataAutocomplete';
import { getDeliveryDesignOptions } from '../lib/deliveryDesignOptions';

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const media = window.matchMedia(query);
    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [query]);
  return matches;
}

export default function OrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const isDesktopView = useMediaQuery('(min-width: 1024px)');
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [transporters, setTransporters] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [lrWarning, setLrWarning] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  
  // Mobile table column toggle: 'delivery' shows Delivered/Cancelled/Pending, 'rate' shows Purchase Rate/Sale Rate/Status
  const [mobileTableView, setMobileTableView] = useState('delivery');
  
  // ALT+E shortcut for export
  useExportShortcut(() => setExportDialogOpen(true));

  // Export handler for this specific order
  const handleExport = async (format) => {
    if (!order) {
      return { success: false, message: 'No order data available to export.' };
    }

    if (format === 'excel') {
      return exportOrderDetailToExcel(order);
    } else {
      return exportOrderDetailToPDF(order);
    }
  };
  
  // Delivery form state - bales now contain fabric_lines array
  const [deliveryForm, setDeliveryForm] = useState({
    delivery_date: new Date().toISOString().split('T')[0],
    lr_date: '',
    transporter_id: '',
    purchase_invoice_number: '',
    sale_invoice_number: '',
    bales: [{ 
      bale_number: '', 
      lr_number: '', 
      fabric_lines: [{ design_number: '', trade_name: '', quantity: '', unit: 'MTRS' }]
    }],
    status: 'Pending',
    remarks: ''
  });

  // Available design numbers from order
  const [availableDesigns, setAvailableDesigns] = useState([]);
  
  // Refs for keyboard navigation
  const formRef = useRef(null);
  const firstBaleInputRef = useRef(null);

  // Smart date parser: converts "27" to "2026-06-27", "2706" to "2026-06-27"
  const parseSmartDate = useCallback((input) => {
    if (!input) return '';
    
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return input;
    
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
    
    // Remove any non-digit characters
    const digits = input.replace(/\D/g, '');
    
    if (digits.length === 1 || digits.length === 2) {
      // Just day: "7" -> "07", "27" -> "27"
      const day = digits.padStart(2, '0');
      if (parseInt(day) >= 1 && parseInt(day) <= 31) {
        return `${currentYear}-${currentMonth}-${day}`;
      }
    } else if (digits.length === 4) {
      // DDMM format: "2706" -> day=27, month=06
      const day = digits.substring(0, 2);
      const month = digits.substring(2, 4);
      if (parseInt(day) >= 1 && parseInt(day) <= 31 && parseInt(month) >= 1 && parseInt(month) <= 12) {
        return `${currentYear}-${month}-${day}`;
      }
    } else if (digits.length === 6) {
      // DDMMYY format: "270626" -> day=27, month=06, year=2026
      const day = digits.substring(0, 2);
      const month = digits.substring(2, 4);
      const year = `20${digits.substring(4, 6)}`;
      if (parseInt(day) >= 1 && parseInt(day) <= 31 && parseInt(month) >= 1 && parseInt(month) <= 12) {
        return `${year}-${month}-${day}`;
      }
    } else if (digits.length === 8) {
      // DDMMYYYY format
      const day = digits.substring(0, 2);
      const month = digits.substring(2, 4);
      const year = digits.substring(4, 8);
      if (parseInt(day) >= 1 && parseInt(day) <= 31 && parseInt(month) >= 1 && parseInt(month) <= 12) {
        return `${year}-${month}-${day}`;
      }
    }
    
    return input; // Return original if no match
  }, []);

  // Handle smart date input with blur conversion
  const handleSmartDateBlur = useCallback((field, value) => {
    const parsedDate = parseSmartDate(value);
    setDeliveryForm(prev => ({ ...prev, [field]: parsedDate }));
  }, [parseSmartDate]);

  useEffect(() => {
    fetchOrder();
    fetchTransporters();
  }, [orderId]);

  useEffect(() => {
    if (order) {
      // Build available designs list from printed and dyed fabrics
      const designs = [];
      
      order.printed_fabrics?.forEach(fabric => {
        if (fabric.design_number) {
          designs.push({
            design_number: fabric.design_number,
            trade_name: fabric.trade_name || '',
            max_quantity: fabric.total_qty || 0,
            fabric_type: 'printed'
          });
        }
      });
      
      order.dyed_fabrics?.forEach(fabric => {
        fabric.colors?.forEach(color => {
          designs.push({
            design_number: `${fabric.trade_name}-${color.color_number}`,
            trade_name: fabric.trade_name,
            max_quantity: color.qty || 0,
            fabric_type: 'dyed'
          });
        });
      });
      
      setAvailableDesigns(designs);
    }
  }, [order]);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const response = await getOrder(orderId);
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransporters = async () => {
    try {
      const response = await getTransporters(true); // Only active transporters
      setTransporters(response.data);
    } catch (error) {
      console.error('Failed to fetch transporters:', error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await getOrderAuditLogs(orderId);
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    }
  };

  const resetDeliveryForm = () => {
    setDeliveryForm({
      delivery_date: new Date().toISOString().split('T')[0],
      lr_date: '',
      transporter_id: '',
      purchase_invoice_number: '',
      sale_invoice_number: '',
      bales: [{ 
        bale_number: '', 
        lr_number: '', 
        fabric_lines: [{ design_number: '', trade_name: '', quantity: '', unit: 'MTRS' }]
      }],
      status: 'Pending',
      remarks: ''
    });
    setEditingDelivery(null);
    setLrWarning(null);
  };

  const openAddDeliveryDialog = () => {
    resetDeliveryForm();
    setDeliveryDialogOpen(true);
  };

  const openEditDeliveryDialog = (delivery) => {
    setEditingDelivery(delivery);
    
    // Convert legacy bale format to new fabric_lines format if needed
    const convertedBales = delivery.bales?.length > 0 
      ? delivery.bales.map(bale => {
          // If bale already has fabric_lines, use them
          if (bale.fabric_lines?.length > 0) {
            return bale;
          }
          // Otherwise, convert legacy format (single design per bale)
          return {
            bale_number: bale.bale_number || '',
            lr_number: bale.lr_number || '',
            fabric_lines: bale.design_number 
              ? [{ 
                  design_number: bale.design_number, 
                  trade_name: bale.trade_name || '', 
                  quantity: bale.quantity || 0,
                  unit: 'MTRS'
                }]
              : [{ design_number: '', trade_name: '', quantity: 0, unit: 'MTRS' }]
          };
        })
      : [{ bale_number: '', lr_number: '', fabric_lines: [{ design_number: '', trade_name: '', quantity: 0, unit: 'MTRS' }] }];
    
    setDeliveryForm({
      delivery_date: delivery.delivery_date || '',
      lr_date: delivery.lr_date || '',
      transporter_id: delivery.transporter_id || '',
      purchase_invoice_number: delivery.purchase_invoice_number || '',
      sale_invoice_number: delivery.sale_invoice_number || '',
      bales: convertedBales,
      status: delivery.status || 'Pending',
      remarks: delivery.remarks || ''
    });
    setDeliveryDialogOpen(true);
  };

  const openAuditLogDialog = async () => {
    await fetchAuditLogs();
    setAuditDialogOpen(true);
  };

  // Bale handlers - now supporting multiple fabric lines per bale
  const addBaleRow = () => {
    setDeliveryForm(prev => ({
      ...prev,
      bales: [...prev.bales, { 
        bale_number: '', 
        lr_number: '', 
        fabric_lines: [{ design_number: '', trade_name: '', quantity: '', unit: 'MTRS' }]
      }]
    }));
  };

  const removeBaleRow = (baleIndex) => {
    setDeliveryForm(prev => ({
      ...prev,
      bales: prev.bales.filter((_, i) => i !== baleIndex)
    }));
  };

  const updateBaleField = (baleIndex, field, value) => {
    setDeliveryForm(prev => ({
      ...prev,
      bales: prev.bales.map((bale, i) => {
        if (i !== baleIndex) return bale;
        return { ...bale, [field]: value };
      })
    }));
  };

  // Fabric line handlers within a bale
  const addFabricLine = (baleIndex) => {
    setDeliveryForm(prev => ({
      ...prev,
      bales: prev.bales.map((bale, i) => {
        if (i !== baleIndex) return bale;
        return {
          ...bale,
          fabric_lines: [...bale.fabric_lines, { design_number: '', trade_name: '', quantity: '', unit: 'MTRS' }]
        };
      })
    }));
  };

  const removeFabricLine = (baleIndex, lineIndex) => {
    setDeliveryForm(prev => ({
      ...prev,
      bales: prev.bales.map((bale, i) => {
        if (i !== baleIndex) return bale;
        return {
          ...bale,
          fabric_lines: bale.fabric_lines.filter((_, j) => j !== lineIndex)
        };
      })
    }));
  };

  const updateFabricLineField = (baleIndex, lineIndex, field, value) => {
    setDeliveryForm(prev => ({
      ...prev,
      bales: prev.bales.map((bale, i) => {
        if (i !== baleIndex) return bale;
        return {
          ...bale,
          fabric_lines: bale.fabric_lines.map((line, j) => {
            if (j !== lineIndex) return line;
            
            // Handle quantity - keep as string for display, will be parsed on save
            if (field === 'quantity') {
              // Allow empty string for blank display, otherwise parse to number
              const numValue = value === '' ? '' : (parseFloat(value) || 0);
              return { ...line, [field]: numValue };
            }
            
            // Handle design selection - also set trade_name
            if (field === 'design_number') {
              const design = availableDesigns.find(d => d.design_number === value);
              return { 
                ...line, 
                design_number: value,
                trade_name: design?.trade_name || ''
              };
            }
            
            return { ...line, [field]: value };
          })
        };
      })
    }));
  };

  // Keyboard shortcut handler for the delivery form
  const handleDeliveryFormKeyDown = useCallback((e) => {
    // Ctrl + Enter: Save delivery
    if (e.ctrlKey && e.key === 'Enter') {
      e.preventDefault();
      handleSaveDelivery(false);
      return;
    }
    
    // Alt + B: Add new bale
    if (e.altKey && e.key.toLowerCase() === 'b') {
      e.preventDefault();
      addBaleRow();
      // Focus the new bale's first input after a short delay
      setTimeout(() => {
        const baleInputs = document.querySelectorAll('[data-testid^="bale-number-input-"]');
        if (baleInputs.length > 0) {
          baleInputs[baleInputs.length - 1].focus();
        }
      }, 50);
      return;
    }
    
    // Alt + L: Add new fabric line to current/last bale
    if (e.altKey && e.key.toLowerCase() === 'l') {
      e.preventDefault();
      const lastBaleIndex = deliveryForm.bales.length - 1;
      addFabricLine(lastBaleIndex);
      return;
    }
    
    // Enter key: Move to next field (within form inputs)
    if (e.key === 'Enter' && !e.ctrlKey && !e.altKey) {
      const target = e.target;
      const tagName = target.tagName.toLowerCase();
      
      // Only handle for input elements, not buttons or selects
      if (tagName === 'input' && target.type !== 'button') {
        e.preventDefault();
        
        // Check if this is a quantity field - if so, add a new fabric line
        const qtyMatch = target.getAttribute('data-testid')?.match(/fabric-line-qty-(\d+)-(\d+)/);
        if (qtyMatch) {
          const baleIndex = parseInt(qtyMatch[1]);
          addFabricLine(baleIndex);
          // Focus will be handled by the new line appearing
          setTimeout(() => {
            const designSelects = document.querySelectorAll(`[data-testid="fabric-line-design-${baleIndex}-${deliveryForm.bales[baleIndex]?.fabric_lines?.length || 0}"]`);
            if (designSelects.length > 0) {
              designSelects[0].click();
            }
          }, 50);
          return;
        }
        
        // Find next focusable element
        const form = formRef.current;
        if (form) {
          const focusableElements = form.querySelectorAll(
            'input:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          const currentIndex = Array.from(focusableElements).indexOf(target);
          if (currentIndex >= 0 && currentIndex < focusableElements.length - 1) {
            focusableElements[currentIndex + 1].focus();
          }
        }
      }
    }
  }, [deliveryForm.bales, addBaleRow, addFabricLine]);

  // Focus first bale input when dialog opens
  useEffect(() => {
    if (deliveryDialogOpen && firstBaleInputRef.current) {
      setTimeout(() => {
        firstBaleInputRef.current?.focus();
      }, 100);
    }
  }, [deliveryDialogOpen]);

  const handleValidateBales = async () => {
    // Validate bales that have at least bale_number or lr_number
    const validBales = deliveryForm.bales.filter(b => b.bale_number || b.lr_number);
    if (validBales.length === 0) return { valid: true, warnings: [] };
    
    try {
      const response = await validateBales(validBales, editingDelivery?.id);
      if (response.data.warnings?.length > 0) {
        setLrWarning(response.data.warnings.join('\n'));
      }
      return response.data;
    } catch (error) {
      console.error('Bale validation error:', error);
      return { valid: false, error: error.response?.data?.detail || 'Validation failed' };
    }
  };

  const handleSaveDelivery = async (skipWarning = false) => {
    const isCancelled = deliveryForm.status === 'Cancelled';
    
    // For Cancelled status: require design, quantity, and remarks
    if (isCancelled) {
      // Get designs from the first bale's first fabric line
      const firstFabricLine = deliveryForm.bales[0]?.fabric_lines?.[0];
      const selectedDesign = firstFabricLine?.design_number;
      const cancelQty = parseFloat(firstFabricLine?.quantity) || 0;
      
      if (!selectedDesign) {
        toast.error('Please select a design for the cancellation');
        return;
      }
      if (cancelQty <= 0) {
        toast.error('Please enter the quantity being cancelled');
        return;
      }
      if (!deliveryForm.remarks || deliveryForm.remarks.trim() === '') {
        toast.error('Please provide remarks for the cancellation');
        return;
      }
      
      setSubmitting(true);
      try {
        const deliveryData = {
          delivery_date: deliveryForm.delivery_date,
          lr_date: '',
          transporter_id: '',
          purchase_invoice_number: '',
          sale_invoice_number: '',
          bales: [{
            bale_number: '-',
            lr_number: '-',
            fabric_lines: [{
              design_number: selectedDesign,
              trade_name: firstFabricLine?.trade_name || '',
              quantity: cancelQty,
              unit: 'MTRS'
            }]
          }],
          status: 'Cancelled',
          remarks: deliveryForm.remarks
        };

        if (editingDelivery) {
          await updateDelivery(orderId, editingDelivery.id, deliveryData);
          toast.success('Cancellation updated successfully!');
        } else {
          await addDelivery(orderId, deliveryData);
          toast.success('Cancellation recorded successfully!');
        }
        setDeliveryDialogOpen(false);
        resetDeliveryForm();
        fetchOrder();
      } catch (error) {
        console.error('Failed to save cancellation:', error);
        toast.error(error.response?.data?.detail || 'Failed to save cancellation');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    
    // For Delivered/Partially Delivered: validate bales have bale_number, lr_number, and at least one valid fabric line
    const validBales = deliveryForm.bales.filter(bale => {
      if (!bale.bale_number || !bale.lr_number) return false;
      // Must have at least one fabric line with design and quantity > 0
      const validLines = bale.fabric_lines?.filter(line => line.design_number && line.quantity > 0) || [];
      return validLines.length > 0;
    });
    
    // Validate bales
    if (validBales.length > 0 && !skipWarning) {
      const validation = await handleValidateBales();
      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }
      if (validation.warnings?.length > 0 && !lrWarning) {
        setLrWarning(validation.warnings.join('\n'));
        return; // Show warning first
      }
    }

    // Calculate total from all fabric lines in all bales
    let totalFromBales = 0;
    validBales.forEach(bale => {
      bale.fabric_lines?.forEach(line => {
        if (line.quantity > 0) {
          totalFromBales += parseFloat(line.quantity) || 0;
        }
      });
    });
    
    if (totalFromBales === 0) {
      toast.error('Please add at least one bale with Bale Number, LR Number, and a fabric line with Design and Quantity');
      return;
    }

    if (!deliveryForm.transporter_id) {
      toast.error('Please select a transporter');
      return;
    }

    setSubmitting(true);
    try {
      // Prepare bales with only valid fabric lines, rounded quantities
      const preparedBales = validBales.map(bale => ({
        bale_number: bale.bale_number,
        lr_number: bale.lr_number,
        fabric_lines: bale.fabric_lines
          .filter(line => line.design_number && line.quantity > 0)
          .map(line => ({
            design_number: line.design_number,
            trade_name: line.trade_name || '',
            quantity: Math.round((parseFloat(line.quantity) || 0) * 100) / 100,
            unit: line.unit || 'MTRS'
          }))
      }));

      const deliveryData = {
        delivery_date: deliveryForm.delivery_date,
        lr_date: deliveryForm.lr_date,
        transporter_id: deliveryForm.transporter_id,
        purchase_invoice_number: deliveryForm.purchase_invoice_number,
        sale_invoice_number: deliveryForm.sale_invoice_number,
        bales: preparedBales,
        status: deliveryForm.status,
        remarks: deliveryForm.remarks
      };

      if (editingDelivery) {
        await updateDelivery(orderId, editingDelivery.id, deliveryData);
        toast.success('Delivery updated successfully!');
      } else {
        await addDelivery(orderId, deliveryData);
        toast.success('Delivery added successfully!');
      }
      setDeliveryDialogOpen(false);
      resetDeliveryForm();
      fetchOrder();
    } catch (error) {
      console.error('Failed to save delivery:', error);
      toast.error(error.response?.data?.detail || 'Failed to save delivery');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDelivery = async (deliveryId) => {
    if (!window.confirm('Are you sure you want to delete this delivery?')) return;
    
    try {
      await deleteDelivery(orderId, deliveryId);
      toast.success('Delivery deleted successfully!');
      fetchOrder();
    } catch (error) {
      console.error('Failed to delete delivery:', error);
      toast.error('Failed to delete delivery');
    }
  };

  const handleExportBuyerPDF = async () => {
    try {
      const response = await exportBuyerPDF(orderId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `buyer_${order.order_details.indent_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Buyer PDF downloaded!');
    } catch (error) {
      console.error('Failed to export Buyer PDF:', error);
      toast.error('Failed to export Buyer PDF');
    }
  };

  const handleExportSupplierPDF = async () => {
    try {
      const response = await exportSupplierPDF(orderId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `supplier_${order.order_details.indent_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Supplier PDF downloaded!');
    } catch (error) {
      console.error('Failed to export Supplier PDF:', error);
      toast.error('Failed to export Supplier PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-maroon-600">Loading order...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-maroon-600">Order not found</div>
      </div>
    );
  }

  // Use backend-calculated summaries as source of truth
  // The backend order_summary contains: design_summaries, total_ordered, total_delivered, total_cancelled, total_pending
  const backendSummary = order.order_summary || {};
  const designSummaries = backendSummary.design_summaries || {};
  
  // Helper to get design summary from backend (for per-row display)
  const getDesignSummary = (designKey) => {
    return designSummaries[designKey] || {
      delivered_qty: 0,
      cancelled_qty: 0,
      pending_qty: 0,
      status: 'Pending'
    };
  };

  // Calculate order-level summary from backend data
  const calculateOrderSummary = () => {
    let primaryUnit = 'mtrs';
    
    // Get unit from fabrics
    order.printed_fabrics?.forEach(fabric => {
      if (fabric.unit) primaryUnit = fabric.unit.toLowerCase();
    });
    order.dyed_fabrics?.forEach(fabric => {
      if (fabric.unit) primaryUnit = fabric.unit.toLowerCase();
    });

    return { 
      totalOrdered: backendSummary.total_ordered || 0, 
      totalDelivered: backendSummary.total_delivered || 0, 
      totalCancelled: backendSummary.total_cancelled || 0, 
      totalPending: backendSummary.total_pending || 0, 
      primaryUnit 
    };
  };

  const orderSummary = calculateOrderSummary();

  // Helper function to calculate item status (uses backend data via getDesignSummary)
  const getItemStatus = (ordered, delivered, cancelled) => {
    if (cancelled >= ordered) return { label: 'Cancelled', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', icon: XCircle };
    if (delivered >= ordered) return { label: 'Delivered', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle2 };
    if (cancelled > 0 && delivered > 0) return { label: 'Partial', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', icon: CircleDot };
    if (cancelled > 0) return { label: 'Partial Cancelled', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', icon: XCircle };
    if (delivered > 0) return { label: 'Partial Delivered', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', icon: Clock };
    return { label: 'Pending', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', icon: Clock };
  };

  // Get overall order status
  const getOverallStatus = () => {
    const { totalOrdered, totalDelivered, totalCancelled, totalPending } = orderSummary;
    if (totalCancelled >= totalOrdered) return 'Cancelled';
    if (totalDelivered >= totalOrdered) return 'Delivered';
    if (totalCancelled > 0 || totalDelivered > 0) return 'Partial';
    return 'Pending';
  };

  return (
    <div className="space-y-2 sm:space-y-6" data-testid="order-detail">
      {/* Header - Matching mockup layout */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-maroon-950">
            {order.order_details.indent_number}
          </h1>
          <p className="text-maroon-700 text-xs sm:text-sm">Order Details & Deliveries</p>
        </div>
        <div className="flex flex-wrap gap-1.5 sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/orders')}
            className="h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm border-maroon-300 hover:bg-maroon-50"
          >
            <ArrowLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            Back
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={openAuditLogDialog}
            className="h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm border-maroon-300 hover:bg-maroon-50"
          >
            <History className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            Audit Trail
          </Button>
          <Button
            data-testid="edit-order-button"
            size="sm"
            onClick={() => navigate(`/orders/edit/${orderId}`)}
            className="h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm bg-maroon-900 hover:bg-maroon-800"
          >
            <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            Edit
          </Button>
        </div>
      </div>

      {/* Order Info Bar with Summary Cards */}
      <div className="border border-maroon-200 rounded-lg bg-white overflow-hidden">
        <div className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            {/* Left side: Indent No. and Date only */}
            <div className="flex items-center gap-x-6 gap-y-2">
              <div>
                <span className="text-maroon-500 text-xs block">Indent No.</span>
                <p className="text-maroon-950 font-semibold text-sm">{order.order_details.indent_number}</p>
              </div>
              <div>
                <span className="text-maroon-500 text-xs block">Date</span>
                <p className="text-maroon-950 font-semibold text-sm">{formatDate(order.order_details.order_date)}</p>
              </div>
            </div>

            {/* Right side: Summary cards */}
            <div className="grid grid-cols-4 gap-2 sm:gap-3 flex-1">
              {/* Ordered Card */}
              <div className="border border-maroon-200 rounded-lg p-2 sm:p-3 text-center bg-maroon-50/30">
                <span className="text-maroon-600 text-[10px] sm:text-xs block">Ordered</span>
                <p className="text-maroon-950 font-bold text-lg sm:text-xl">{orderSummary.totalOrdered.toFixed(0)}</p>
                <span className="text-maroon-500 text-[10px] sm:text-xs">{orderSummary.primaryUnit}</span>
              </div>
              {/* Delivered Card */}
              <div className="border border-emerald-200 rounded-lg p-2 sm:p-3 text-center bg-emerald-50/50">
                <span className="text-emerald-600 text-[10px] sm:text-xs block">Delivered</span>
                <p className="text-emerald-700 font-bold text-lg sm:text-xl">{orderSummary.totalDelivered.toFixed(0)}</p>
                <span className="text-emerald-500 text-[10px] sm:text-xs">{orderSummary.primaryUnit}</span>
              </div>
              {/* Cancelled Card */}
              <div className="border border-red-200 rounded-lg p-2 sm:p-3 text-center bg-red-50/50">
                <span className="text-red-600 text-[10px] sm:text-xs block">Cancelled</span>
                <p className="text-red-700 font-bold text-lg sm:text-xl">{orderSummary.totalCancelled.toFixed(0)}</p>
                <span className="text-red-500 text-[10px] sm:text-xs">{orderSummary.primaryUnit}</span>
              </div>
              {/* Pending Card */}
              <div className="border border-amber-200 rounded-lg p-2 sm:p-3 text-center bg-amber-50/50">
                <span className="text-amber-600 text-[10px] sm:text-xs block">Pending</span>
                <p className="text-amber-700 font-bold text-lg sm:text-xl">{orderSummary.totalPending.toFixed(0)}</p>
                <span className="text-amber-500 text-[10px] sm:text-xs">{orderSummary.primaryUnit}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Buyers & Suppliers Row - Matching mockup style */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
        {order.buyers?.length > 0 && (
          <div className="border border-maroon-200 rounded-lg bg-white p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <User className="h-4 w-4 text-maroon-500" />
              <span className="text-maroon-500 text-xs font-medium">Buyer</span>
            </div>
            {order.buyers.map((buyer, index) => (
              <p key={index} className="font-semibold text-maroon-950 text-sm sm:text-base">{buyer.buyer_name}</p>
            ))}
          </div>
        )}

        {order.suppliers?.length > 0 && (
          <div className="border border-maroon-200 rounded-lg bg-white p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="h-4 w-4 text-maroon-500" />
              <span className="text-maroon-500 text-xs font-medium">Supplier</span>
            </div>
            {order.suppliers.map((supplier, index) => (
              <p key={index} className="font-semibold text-maroon-950 text-sm sm:text-base">{supplier.supplier_name}</p>
            ))}
          </div>
        )}
      </div>

      {/* Printed Fabrics - Matching mockup design */}
      {order.printed_fabrics?.length > 0 && (
        <div className="border border-maroon-200 border-l-4 border-l-rose-500 rounded-lg bg-white">
          <div className="bg-rose-50/50 px-3 py-2 sm:px-4 sm:py-3 border-b border-rose-100">
            <h3 className="text-sm sm:text-lg font-semibold text-rose-900">Printed Fabrics</h3>
          </div>
          <div className="p-2 sm:p-4">
            {/* Desktop Table - All columns */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-maroon-200 bg-gray-50">
                    <th className="text-left py-2 px-3 font-semibold text-maroon-700">Design No.</th>
                    <th className="text-center py-2 px-3 font-semibold text-maroon-700">
                      <div>Ordered</div>
                      <div className="text-[10px] font-normal text-maroon-500">(mtrs)</div>
                    </th>
                    <th className="text-center py-2 px-3 font-semibold text-emerald-700">Delivered</th>
                    <th className="text-center py-2 px-3 font-semibold text-red-600">Cancelled</th>
                    <th className="text-center py-2 px-3 font-semibold text-amber-600">Pending</th>
                    <th className="text-right py-2 px-3 font-semibold text-maroon-700">
                      <div>Purchase Rate</div>
                      <div className="text-[10px] font-normal text-maroon-500">(₹ per mtr)</div>
                    </th>
                    <th className="text-right py-2 px-3 font-semibold text-maroon-700">
                      <div>Sale Rate</div>
                      <div className="text-[10px] font-normal text-maroon-500">(₹ per mtr)</div>
                    </th>
                    <th className="text-center py-2 px-3 font-semibold text-maroon-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {order.printed_fabrics.map((fabric, index) => {
                    const ordered = fabric.total_qty || 0;
                    const summary = getDesignSummary(fabric.design_number);
                    const delivered = summary.delivered_qty;
                    const cancelled = summary.cancelled_qty;
                    const pending = summary.pending_qty;
                    const unit = (fabric.unit || 'MTRS').toLowerCase();
                    const status = getItemStatus(ordered, delivered, cancelled);
                    const StatusIcon = status.icon;
                    
                    return (
                      <tr key={index} className="border-b border-maroon-100 hover:bg-gray-50">
                        <td className="py-2 px-3 text-maroon-950 font-medium">
                          <div>{fabric.trade_name}</div>
                          <div className="text-maroon-600 text-xs">{fabric.design_number}</div>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <span className="font-bold text-maroon-950">{ordered.toFixed(0)}</span>
                          <span className="text-maroon-400 text-[10px] ml-1">{unit}</span>
                        </td>
                        <td className="py-2 px-3 text-emerald-700 font-semibold text-center">{delivered.toFixed(0)}</td>
                        <td className="py-2 px-3 text-red-600 font-semibold text-center">{cancelled.toFixed(0)}</td>
                        <td className="py-2 px-3 text-amber-600 font-semibold text-center">{pending.toFixed(0)}</td>
                        <td className="py-2 px-3 text-maroon-700 text-right">
                          ₹{fabric.purchase_rate || 0}
                          {fabric.purchase_cd_percent > 0 && (
                            <div className="text-[10px] text-emerald-600 font-medium">CD {fabric.purchase_cd_percent}%</div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-maroon-700 text-right">
                          ₹{fabric.sales_rate || 0}
                          {fabric.sales_cd_percent > 0 && (
                            <div className="text-[10px] text-blue-600 font-medium">CD {fabric.sales_cd_percent}%</div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full border ${status.bg} ${status.border} ${status.color}`}>
                            <StatusIcon className="h-3 w-3" />
                            {status.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {/* Total Row */}
                  <tr className="bg-gray-100 font-semibold">
                    <td className="py-2 px-3 text-maroon-950">Total</td>
                    <td className="py-2 px-3 text-center">
                      <span className="font-bold">{order.printed_fabrics.reduce((sum, f) => sum + (f.total_qty || 0), 0).toFixed(0)}</span>
                      <span className="text-maroon-400 text-[10px] ml-1">mtrs</span>
                    </td>
                    <td className="py-2 px-3 text-emerald-700 text-center">
                      {order.printed_fabrics.reduce((sum, f) => sum + (getDesignSummary(f.design_number).delivered_qty || 0), 0).toFixed(0)}
                    </td>
                    <td className="py-2 px-3 text-red-600 text-center">
                      {order.printed_fabrics.reduce((sum, f) => sum + (getDesignSummary(f.design_number).cancelled_qty || 0), 0).toFixed(0)}
                    </td>
                    <td className="py-2 px-3 text-amber-600 text-center">
                      {order.printed_fabrics.reduce((sum, f) => sum + (getDesignSummary(f.design_number).pending_qty || 0), 0).toFixed(0)}
                    </td>
                    <td className="py-2 px-3 text-maroon-500 text-right">-</td>
                    <td className="py-2 px-3 text-maroon-500 text-right">-</td>
                    <td className="py-2 px-3 text-center">
                      <span className="text-xs text-maroon-600 font-medium">Overall: {getOverallStatus()}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            {/* Mobile Table - Toggle columns */}
            <div className="sm:hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-maroon-200 bg-gray-50">
                    <th className="text-left py-2 px-1.5 font-semibold text-maroon-700 w-[35%]">Design</th>
                    <th className="text-center py-2 px-1 font-semibold text-maroon-700 w-[17%]">Ord</th>
                    <th 
                      colSpan={3} 
                      className="py-2 px-1 font-semibold cursor-pointer select-none active:bg-gray-100"
                      onClick={() => setMobileTableView(mobileTableView === 'delivery' ? 'rate' : 'delivery')}
                    >
                      {mobileTableView === 'delivery' ? (
                        <div className="flex justify-around">
                          <span className="text-emerald-700">Del</span>
                          <span className="text-red-600">Can</span>
                          <span className="text-amber-600">Pen</span>
                        </div>
                      ) : (
                        <div className="flex justify-around">
                          <span className="text-maroon-700">P.Rate</span>
                          <span className="text-maroon-700">S.Rate</span>
                          <span className="text-maroon-700">Status</span>
                        </div>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {order.printed_fabrics.map((fabric, index) => {
                    const ordered = fabric.total_qty || 0;
                    const summary = getDesignSummary(fabric.design_number);
                    const delivered = summary.delivered_qty;
                    const cancelled = summary.cancelled_qty;
                    const pending = summary.pending_qty;
                    const status = getItemStatus(ordered, delivered, cancelled);
                    
                    return (
                      <tr key={index} className="border-b border-maroon-100">
                        <td className="py-1.5 px-1.5 text-maroon-950">
                          <div className="font-medium truncate">{fabric.trade_name}</div>
                          <div className="text-maroon-500 text-[10px]">{fabric.design_number}</div>
                        </td>
                        <td className="py-1.5 px-1 text-center font-bold text-maroon-950">{ordered.toFixed(0)}</td>
                        {mobileTableView === 'delivery' ? (
                          <>
                            <td className="py-1.5 px-1 text-emerald-700 font-semibold text-center w-[16%]">{delivered.toFixed(0)}</td>
                            <td className="py-1.5 px-1 text-red-600 font-semibold text-center w-[16%]">{cancelled.toFixed(0)}</td>
                            <td className="py-1.5 px-1 text-amber-600 font-semibold text-center w-[16%]">{pending.toFixed(0)}</td>
                          </>
                        ) : (
                          <>
                            <td className="py-1.5 px-1 text-maroon-700 text-center w-[16%]">
                              ₹{fabric.purchase_rate || 0}
                              {fabric.purchase_cd_percent > 0 && (
                                <div className="text-[9px] text-emerald-600">CD{fabric.purchase_cd_percent}%</div>
                              )}
                            </td>
                            <td className="py-1.5 px-1 text-maroon-700 text-center w-[16%]">
                              ₹{fabric.sales_rate || 0}
                              {fabric.sales_cd_percent > 0 && (
                                <div className="text-[9px] text-blue-600">CD{fabric.sales_cd_percent}%</div>
                              )}
                            </td>
                            <td className="py-1.5 px-1 text-center w-[16%]">
                              <span className={`text-[10px] ${status.color}`}>{status.label.split(' ')[0]}</span>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                  {/* Total Row */}
                  <tr className="bg-gray-100 font-semibold">
                    <td className="py-1.5 px-1.5 text-maroon-950">Total</td>
                    <td className="py-1.5 px-1 text-center font-bold">{order.printed_fabrics.reduce((sum, f) => sum + (f.total_qty || 0), 0).toFixed(0)}</td>
                    {mobileTableView === 'delivery' ? (
                      <>
                        <td className="py-1.5 px-1 text-emerald-700 text-center">
                          {order.printed_fabrics.reduce((sum, f) => sum + (getDesignSummary(f.design_number).delivered_qty || 0), 0).toFixed(0)}
                        </td>
                        <td className="py-1.5 px-1 text-red-600 text-center">
                          {order.printed_fabrics.reduce((sum, f) => sum + (getDesignSummary(f.design_number).cancelled_qty || 0), 0).toFixed(0)}
                        </td>
                        <td className="py-1.5 px-1 text-amber-600 text-center">
                          {order.printed_fabrics.reduce((sum, f) => sum + (getDesignSummary(f.design_number).pending_qty || 0), 0).toFixed(0)}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-1.5 px-1 text-maroon-500 text-center">-</td>
                        <td className="py-1.5 px-1 text-maroon-500 text-center">-</td>
                        <td className="py-1.5 px-1 text-center">
                          <span className="text-[10px] text-maroon-600">{getOverallStatus()}</span>
                        </td>
                      </>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Dyed Fabrics - Matching mockup design */}
      {order.dyed_fabrics?.length > 0 && (
        <div className="border border-maroon-200 border-l-4 border-l-purple-500 rounded-lg bg-white">
          <div className="bg-purple-50/50 px-3 py-2 sm:px-4 sm:py-3 border-b border-purple-100">
            <h3 className="text-sm sm:text-lg font-semibold text-purple-900">Dyed Fabrics</h3>
          </div>
          <div className="p-2 sm:p-4 space-y-4">
              {order.dyed_fabrics.map((fabric, index) => {
                // Check if this is Fixed Chart mode
                const isFixedChart = fabric.mode === 'fixed_chart' || 
                  (fabric.total_qty > 0 && fabric.qty_per_matching > 0 && (!fabric.colors || fabric.colors.length === 0));
                
                const unit = (fabric.unit || 'MTRS').toLowerCase();
                
                if (isFixedChart) {
                  // Fixed Chart Mode display
                  const totalQty = fabric.total_qty || (fabric.qty_per_matching * fabric.matching) || 0;
                  const designKey = `${fabric.trade_name}-${fabric.design_number || 'FC'}`;
                  const summary = getDesignSummary(designKey);
                  const delivered = summary.delivered_qty;
                  const cancelled = summary.cancelled_qty;
                  const pending = summary.pending_qty;
                  const status = getItemStatus(totalQty, delivered, cancelled);
                  const StatusIcon = status.icon;
                  
                  return (
                    <div key={index} className="border border-purple-200 rounded-lg overflow-hidden">
                      <div className="bg-purple-50/50 px-3 py-2 border-b border-purple-100">
                        <span className="font-bold text-maroon-950 text-sm sm:text-base">{fabric.trade_name}</span>
                      </div>
                      <div className="p-2 sm:p-3">
                        {/* Desktop Table */}
                        <div className="hidden sm:block">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-maroon-200 bg-gray-50">
                                <th className="text-left py-2 px-2 font-semibold text-maroon-700">Design</th>
                                <th className="text-center py-2 px-2 font-semibold text-maroon-700">Ordered</th>
                                <th className="text-center py-2 px-2 font-semibold text-emerald-700">Delivered</th>
                                <th className="text-center py-2 px-2 font-semibold text-red-600">Cancelled</th>
                                <th className="text-center py-2 px-2 font-semibold text-amber-600">Pending</th>
                                <th className="text-right py-2 px-2 font-semibold text-maroon-700">Purchase Rate</th>
                                <th className="text-right py-2 px-2 font-semibold text-maroon-700">Sale Rate</th>
                                <th className="text-center py-2 px-2 font-semibold text-maroon-700">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b border-maroon-100">
                                <td className="py-2 px-2 text-maroon-950">{fabric.design_number || 'FC'}</td>
                                <td className="py-2 px-2 text-center">
                                  <span className="font-bold">{totalQty.toFixed(0)}</span>
                                  <span className="text-maroon-400 text-[10px] ml-1">{unit}</span>
                                </td>
                                <td className="py-2 px-2 text-emerald-700 font-semibold text-center">{delivered.toFixed(0)}</td>
                                <td className="py-2 px-2 text-red-600 font-semibold text-center">{cancelled.toFixed(0)}</td>
                                <td className="py-2 px-2 text-amber-600 font-semibold text-center">{pending.toFixed(0)}</td>
                                <td className="py-2 px-2 text-maroon-700 text-right">
                                  ₹{fabric.purchase_rate || 0}
                                  {fabric.purchase_cd_percent > 0 && (
                                    <div className="text-[10px] text-emerald-600 font-medium">CD {fabric.purchase_cd_percent}%</div>
                                  )}
                                </td>
                                <td className="py-2 px-2 text-maroon-700 text-right">
                                  ₹{fabric.sales_rate || 0}
                                  {fabric.sales_cd_percent > 0 && (
                                    <div className="text-[10px] text-blue-600 font-medium">CD {fabric.sales_cd_percent}%</div>
                                  )}
                                </td>
                                <td className="py-2 px-2 text-center">
                                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full border ${status.bg} ${status.border} ${status.color}`}>
                                    <StatusIcon className="h-3 w-3" />
                                    {status.label}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Mobile Table */}
                        <div className="sm:hidden">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-maroon-200 bg-gray-50">
                                <th className="text-left py-2 px-1.5 font-semibold text-maroon-700 w-[35%]">Design</th>
                                <th className="text-center py-2 px-1 font-semibold text-maroon-700 w-[17%]">Ord</th>
                                <th 
                                  colSpan={3} 
                                  className="py-2 px-1 font-semibold cursor-pointer select-none active:bg-gray-100"
                                  onClick={() => setMobileTableView(mobileTableView === 'delivery' ? 'rate' : 'delivery')}
                                >
                                  {mobileTableView === 'delivery' ? (
                                    <div className="flex justify-around">
                                      <span className="text-emerald-700">Del</span>
                                      <span className="text-red-600">Can</span>
                                      <span className="text-amber-600">Pen</span>
                                    </div>
                                  ) : (
                                    <div className="flex justify-around">
                                      <span className="text-maroon-700">P.Rate</span>
                                      <span className="text-maroon-700">S.Rate</span>
                                      <span className="text-maroon-700">Status</span>
                                    </div>
                                  )}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b border-maroon-100">
                                <td className="py-1.5 px-1.5 text-maroon-950 font-medium">{fabric.design_number || 'FC'}</td>
                                <td className="py-1.5 px-1 text-center font-bold">{totalQty.toFixed(0)}</td>
                                {mobileTableView === 'delivery' ? (
                                  <>
                                    <td className="py-1.5 px-1 text-emerald-700 font-semibold text-center w-[16%]">{delivered.toFixed(0)}</td>
                                    <td className="py-1.5 px-1 text-red-600 font-semibold text-center w-[16%]">{cancelled.toFixed(0)}</td>
                                    <td className="py-1.5 px-1 text-amber-600 font-semibold text-center w-[16%]">{pending.toFixed(0)}</td>
                                  </>
                                ) : (
                                  <>
                                    <td className="py-1.5 px-1 text-maroon-700 text-center w-[16%]">
                                      ₹{fabric.purchase_rate || 0}
                                      {fabric.purchase_cd_percent > 0 && (
                                        <div className="text-[9px] text-emerald-600">CD{fabric.purchase_cd_percent}%</div>
                                      )}
                                    </td>
                                    <td className="py-1.5 px-1 text-maroon-700 text-center w-[16%]">
                                      ₹{fabric.sales_rate || 0}
                                      {fabric.sales_cd_percent > 0 && (
                                        <div className="text-[9px] text-blue-600">CD{fabric.sales_cd_percent}%</div>
                                      )}
                                    </td>
                                    <td className="py-1.5 px-1 text-center w-[16%]">
                                      <span className={`text-[10px] ${status.color}`}>{status.label.split(' ')[0]}</span>
                                    </td>
                                  </>
                                )}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                // Pick & Choose Mode - Color table matching mockup
                const tradeTotal = fabric.colors?.reduce((sum, c) => sum + (c.qty || 0), 0) || 0;
                const tradeDelivered = fabric.colors?.reduce((sum, c) => {
                  const designKey = `${fabric.trade_name}-${c.color_number}`;
                  return sum + (getDesignSummary(designKey).delivered_qty || 0);
                }, 0) || 0;
                const tradeCancelled = fabric.colors?.reduce((sum, c) => {
                  const designKey = `${fabric.trade_name}-${c.color_number}`;
                  return sum + (getDesignSummary(designKey).cancelled_qty || 0);
                }, 0) || 0;
                const tradePending = fabric.colors?.reduce((sum, c) => {
                  const designKey = `${fabric.trade_name}-${c.color_number}`;
                  return sum + (getDesignSummary(designKey).pending_qty || 0);
                }, 0) || 0;
                
                return (
                  <div key={index} className="border border-purple-200 rounded-lg overflow-hidden">
                    <div className="bg-purple-50/50 px-3 py-2 border-b border-purple-100">
                      <span className="font-bold text-maroon-950 text-sm sm:text-base">{fabric.trade_name}</span>
                    </div>
                    
                    {/* Desktop Table - All columns */}
                    <div className="hidden sm:block">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-maroon-200 bg-gray-50">
                            <th className="text-left py-2 px-3 font-semibold text-maroon-700">Color No.</th>
                            <th className="text-center py-2 px-3 font-semibold text-maroon-700">
                              <div>Ordered</div>
                              <div className="text-[10px] font-normal text-maroon-500">({unit})</div>
                            </th>
                            <th className="text-center py-2 px-3 font-semibold text-emerald-700">Delivered</th>
                            <th className="text-center py-2 px-3 font-semibold text-red-600">Cancelled</th>
                            <th className="text-center py-2 px-3 font-semibold text-amber-600">Pending</th>
                            <th className="text-right py-2 px-3 font-semibold text-maroon-700">
                              <div>Purchase Rate</div>
                              <div className="text-[10px] font-normal text-maroon-500">(₹ per {unit.slice(0, 3)})</div>
                            </th>
                            <th className="text-right py-2 px-3 font-semibold text-maroon-700">
                              <div>Sale Rate</div>
                              <div className="text-[10px] font-normal text-maroon-500">(₹ per {unit.slice(0, 3)})</div>
                            </th>
                            <th className="text-center py-2 px-3 font-semibold text-maroon-700">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fabric.colors?.map((color, colorIndex) => {
                            const designKey = `${fabric.trade_name}-${color.color_number}`;
                            const ordered = color.qty || 0;
                            const summary = getDesignSummary(designKey);
                            const delivered = summary.delivered_qty;
                            const cancelled = summary.cancelled_qty;
                            const pending = summary.pending_qty;
                            const status = getItemStatus(ordered, delivered, cancelled);
                            const StatusIcon = status.icon;
                            
                            const colorDisplay = color.color_name 
                              ? `${color.color_number} (${color.color_name})`
                              : color.color_number;
                              
                            return (
                              <tr key={colorIndex} className="border-b border-maroon-100 hover:bg-gray-50">
                                <td className="py-2 px-3 text-maroon-950 font-medium">{colorDisplay}</td>
                                <td className="py-2 px-3 text-center">
                                  <span className="font-bold">{ordered.toFixed(0)}</span>
                                  <span className="text-maroon-400 text-[10px] ml-1">{unit}</span>
                                </td>
                                <td className="py-2 px-3 text-emerald-700 font-semibold text-center">{delivered.toFixed(0)}</td>
                                <td className="py-2 px-3 text-red-600 font-semibold text-center">{cancelled.toFixed(0)}</td>
                                <td className="py-2 px-3 text-amber-600 font-semibold text-center">{pending.toFixed(0)}</td>
                                <td className="py-2 px-3 text-maroon-700 text-right">
                                  ₹{color.purchase_rate || 0}
                                  {fabric.trade_purchase_cd_percent > 0 && (
                                    <div className="text-[10px] text-emerald-600 font-medium">CD {fabric.trade_purchase_cd_percent}%</div>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-maroon-700 text-right">
                                  ₹{color.sales_rate || 0}
                                  {fabric.trade_sales_cd_percent > 0 && (
                                    <div className="text-[10px] text-blue-600 font-medium">CD {fabric.trade_sales_cd_percent}%</div>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-center">
                                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full border ${status.bg} ${status.border} ${status.color}`}>
                                    <StatusIcon className="h-3 w-3" />
                                    {status.label}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                          {/* Total Row */}
                          <tr className="bg-gray-100 font-semibold">
                            <td className="py-2 px-3 text-maroon-950">Total</td>
                            <td className="py-2 px-3 text-center">
                              <span className="font-bold">{tradeTotal.toFixed(0)}</span>
                              <span className="text-maroon-400 text-[10px] ml-1">{unit}</span>
                            </td>
                            <td className="py-2 px-3 text-emerald-700 text-center">{tradeDelivered.toFixed(0)}</td>
                            <td className="py-2 px-3 text-red-600 text-center">{tradeCancelled.toFixed(0)}</td>
                            <td className="py-2 px-3 text-amber-600 text-center">{tradePending.toFixed(0)}</td>
                            <td className="py-2 px-3 text-maroon-500 text-right">-</td>
                            <td className="py-2 px-3 text-maroon-500 text-right">-</td>
                            <td className="py-2 px-3 text-center">
                              <span className="text-xs text-maroon-600 font-medium">
                                Overall: {getItemStatus(tradeTotal, tradeDelivered, tradeCancelled).label}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Mobile Table - Toggle columns */}
                    <div className="sm:hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-maroon-200 bg-gray-50">
                            <th className="text-left py-2 px-1.5 font-semibold text-maroon-700 w-[35%]">Color</th>
                            <th className="text-center py-2 px-1 font-semibold text-maroon-700 w-[17%]">Ord</th>
                            <th 
                              colSpan={3} 
                              className="py-2 px-1 font-semibold cursor-pointer select-none active:bg-gray-100"
                              onClick={() => setMobileTableView(mobileTableView === 'delivery' ? 'rate' : 'delivery')}
                            >
                              {mobileTableView === 'delivery' ? (
                                <div className="flex justify-around">
                                  <span className="text-emerald-700">Del</span>
                                  <span className="text-red-600">Can</span>
                                  <span className="text-amber-600">Pen</span>
                                </div>
                              ) : (
                                <div className="flex justify-around">
                                  <span className="text-maroon-700">P.Rate</span>
                                  <span className="text-maroon-700">S.Rate</span>
                                  <span className="text-maroon-700">Status</span>
                                </div>
                              )}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {fabric.colors?.map((color, colorIndex) => {
                            const designKey = `${fabric.trade_name}-${color.color_number}`;
                            const ordered = color.qty || 0;
                            const summary = getDesignSummary(designKey);
                            const delivered = summary.delivered_qty;
                            const cancelled = summary.cancelled_qty;
                            const pending = summary.pending_qty;
                            const status = getItemStatus(ordered, delivered, cancelled);
                            
                            return (
                              <tr key={colorIndex} className="border-b border-maroon-100">
                                <td className="py-1.5 px-1.5 text-maroon-950">
                                  <div className="font-medium truncate">{color.color_number}</div>
                                  {color.color_name && <div className="text-maroon-500 text-[10px] truncate">{color.color_name}</div>}
                                </td>
                                <td className="py-1.5 px-1 text-center font-bold text-maroon-950">{ordered.toFixed(0)}</td>
                                {mobileTableView === 'delivery' ? (
                                  <>
                                    <td className="py-1.5 px-1 text-emerald-700 font-semibold text-center w-[16%]">{delivered.toFixed(0)}</td>
                                    <td className="py-1.5 px-1 text-red-600 font-semibold text-center w-[16%]">{cancelled.toFixed(0)}</td>
                                    <td className="py-1.5 px-1 text-amber-600 font-semibold text-center w-[16%]">{pending.toFixed(0)}</td>
                                  </>
                                ) : (
                                  <>
                                    <td className="py-1.5 px-1 text-maroon-700 text-center w-[16%]">
                                      ₹{color.purchase_rate || 0}
                                      {fabric.trade_purchase_cd_percent > 0 && (
                                        <div className="text-[9px] text-emerald-600">CD{fabric.trade_purchase_cd_percent}%</div>
                                      )}
                                    </td>
                                    <td className="py-1.5 px-1 text-maroon-700 text-center w-[16%]">
                                      ₹{color.sales_rate || 0}
                                      {fabric.trade_sales_cd_percent > 0 && (
                                        <div className="text-[9px] text-blue-600">CD{fabric.trade_sales_cd_percent}%</div>
                                      )}
                                    </td>
                                    <td className="py-1.5 px-1 text-center w-[16%]">
                                      <span className={`text-[10px] ${status.color}`}>{status.label.split(' ')[0]}</span>
                                    </td>
                                  </>
                                )}
                              </tr>
                            );
                          })}
                          {/* Total Row */}
                          <tr className="bg-gray-100 font-semibold">
                            <td className="py-1.5 px-1.5 text-maroon-950">Total</td>
                            <td className="py-1.5 px-1 text-center font-bold">{tradeTotal.toFixed(0)}</td>
                            {mobileTableView === 'delivery' ? (
                              <>
                                <td className="py-1.5 px-1 text-emerald-700 text-center">{tradeDelivered.toFixed(0)}</td>
                                <td className="py-1.5 px-1 text-red-600 text-center">{tradeCancelled.toFixed(0)}</td>
                                <td className="py-1.5 px-1 text-amber-600 text-center">{tradePending.toFixed(0)}</td>
                              </>
                            ) : (
                              <>
                                <td className="py-1.5 px-1 text-maroon-500 text-center">-</td>
                                <td className="py-1.5 px-1 text-maroon-500 text-center">-</td>
                                <td className="py-1.5 px-1 text-center">
                                  <span className="text-[10px] text-maroon-600">{getItemStatus(tradeTotal, tradeDelivered, tradeCancelled).label.split(' ')[0]}</span>
                                </td>
                              </>
                            )}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Deliveries Section - Compact on mobile */}
      <div className="border border-maroon-200 border-l-2 sm:border-l-4 border-l-cyan-500 rounded sm:rounded-lg bg-white">
        <div className="bg-cyan-50/50 px-2 py-1.5 sm:px-4 sm:py-3 border-b border-cyan-100 flex items-center justify-between">
          <h3 className="text-sm sm:text-lg font-semibold text-cyan-900">Deliveries</h3>
          <Button
            data-testid="add-delivery-button"
            size="sm"
            onClick={openAddDeliveryDialog}
            className="h-7 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm bg-cyan-600 hover:bg-cyan-700"
          >
            <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
            Add
          </Button>
        </div>
        <div className="p-1.5 sm:p-4">
          {order.deliveries?.length === 0 ? (
            <div className="text-center py-4 sm:py-8 text-maroon-600">
              <Package className="h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-2 sm:mb-3 text-maroon-400" />
              <p className="text-xs sm:text-base">No deliveries recorded yet</p>
            </div>
          ) : isDesktopView ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-maroon-200 bg-maroon-50/60 text-left text-maroon-700">
                    <th className="px-2 py-2 font-semibold whitespace-nowrap">Phase</th>
                    <th className="px-2 py-2 font-semibold whitespace-nowrap">Status</th>
                    <th className="px-2 py-2 font-semibold whitespace-nowrap">Delivery Date</th>
                    <th className="px-2 py-2 font-semibold whitespace-nowrap">LR Date</th>
                    <th className="px-2 py-2 font-semibold whitespace-nowrap">Transporter</th>
                    <th className="px-2 py-2 font-semibold whitespace-nowrap text-right">Qty Delivered</th>
                    <th className="px-2 py-2 font-semibold whitespace-nowrap">Purchase Invoice</th>
                    <th className="px-2 py-2 font-semibold whitespace-nowrap">Sale Invoice</th>
                    <th className="px-2 py-2 font-semibold whitespace-nowrap">Bale No.</th>
                    <th className="px-2 py-2 font-semibold whitespace-nowrap">LR No.</th>
                    <th className="px-2 py-2 font-semibold">Remarks</th>
                    <th className="px-2 py-2 font-semibold whitespace-nowrap text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-maroon-100">
                  {order.deliveries.map((delivery) => {
                    const bales = delivery.bales || [];
                    return (
                      <tr
                        key={delivery.id}
                        data-testid={`delivery-row-${delivery.id}`}
                        className={`align-top ${delivery.status === 'Cancelled' ? 'bg-red-50' : 'hover:bg-maroon-50/40'}`}
                      >
                        <td className="px-2 py-2 font-semibold text-maroon-950 whitespace-nowrap">Phase {delivery.phase_number}</td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <span className={`px-2 py-0.5 text-xs rounded border ${getStatusColor(delivery.status)}`}>{delivery.status}</span>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-maroon-950">{formatDate(delivery.delivery_date) || '-'}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-maroon-950">{formatDate(delivery.lr_date) || '-'}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-maroon-950 max-w-[140px] truncate" title={delivery.transporter_name || ''}>{delivery.transporter_name || '-'}</td>
                        <td className="px-2 py-2 text-right font-semibold text-emerald-700 whitespace-nowrap">{delivery.total_delivered_quantity || 0}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-maroon-950">{delivery.purchase_invoice_number || '-'}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-maroon-950">{delivery.sale_invoice_number || '-'}</td>
                        <td className="px-2 py-2 text-maroon-950">
                          {bales.length > 0 ? bales.map((b, idx) => <div key={idx} className="whitespace-nowrap">{b.bale_number || '-'}</div>) : '-'}
                        </td>
                        <td className="px-2 py-2 text-maroon-950">
                          {bales.length > 0 ? bales.map((b, idx) => <div key={idx} className="whitespace-nowrap">{b.lr_number || '-'}</div>) : '-'}
                        </td>
                        <td className="px-2 py-2 text-maroon-700 max-w-[200px] truncate" title={delivery.remarks || ''}>{delivery.remarks || '-'}</td>
                        <td className="px-2 py-2 text-center whitespace-nowrap">
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`edit-delivery-${delivery.id}`}
                            onClick={() => openEditDeliveryDialog(delivery)}
                            className="h-7 w-7 p-0 text-maroon-600 hover:bg-maroon-50"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`delete-delivery-${delivery.id}`}
                            onClick={() => handleDeleteDelivery(delivery.id)}
                            className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="space-y-1.5 sm:space-y-4">
              {order.deliveries.map((delivery) => (
                <div 
                  key={delivery.id} 
                  className={`border rounded sm:rounded-lg p-1.5 sm:p-4 ${
                    delivery.status === 'Cancelled' ? 'bg-red-50 border-red-200' : 'border-maroon-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5 sm:mb-3">
                    <div className="flex items-center gap-1.5 sm:gap-3">
                      <span className="text-sm sm:text-lg font-semibold text-maroon-950">Phase {delivery.phase_number}</span>
                      <span className={`px-1.5 py-0.5 sm:px-2 sm:py-1 text-[10px] sm:text-xs rounded border ${getStatusColor(delivery.status)}`}>
                        {delivery.status}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDeliveryDialog(delivery)}
                        className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-maroon-600 hover:bg-maroon-50"
                      >
                        <Pencil className="h-3 w-3 sm:h-4 sm:w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteDelivery(delivery.id)}
                        className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-2 gap-y-1 sm:gap-3 text-[10px] sm:text-sm mb-1.5 sm:mb-3">
                    <div>
                      <span className="text-maroon-600 block">Del Date</span>
                      <p className="font-medium text-maroon-950">{formatDate(delivery.delivery_date) || '-'}</p>
                    </div>
                    <div>
                      <span className="text-maroon-600 block">LR Date</span>
                      <p className="font-medium text-maroon-950">{formatDate(delivery.lr_date) || '-'}</p>
                    </div>
                    <div>
                      <span className="text-maroon-600 block">Transporter</span>
                      <p className="font-medium text-maroon-950 truncate">{delivery.transporter_name || '-'}</p>
                    </div>
                    <div>
                      <span className="text-maroon-600 block">Qty Del</span>
                      <p className="font-semibold text-emerald-700">{delivery.total_delivered_quantity || 0}</p>
                    </div>
                    <div className="hidden sm:block">
                      <span className="text-maroon-600 block">Purch Invoice</span>
                      <p className="font-medium text-maroon-950">{delivery.purchase_invoice_number || '-'}</p>
                    </div>
                    <div className="hidden sm:block">
                      <span className="text-maroon-600 block">Sale Invoice</span>
                      <p className="font-medium text-maroon-950">{delivery.sale_invoice_number || '-'}</p>
                    </div>
                  </div>
                  
                  {/* Bales Section - Compact on mobile */}
                  {delivery.bales?.length > 0 && (
                    <div className="mt-1.5 sm:mt-3 pt-1.5 sm:pt-3 border-t border-maroon-100">
                      <span className="text-[10px] sm:text-xs text-maroon-600 mb-1 block">Bales ({delivery.bales.length})</span>
                      <div className="space-y-1 sm:space-y-2">
                        {delivery.bales.map((bale, idx) => {
                          const fabricLines = bale.fabric_lines?.length > 0 
                            ? bale.fabric_lines 
                            : bale.design_number 
                              ? [{ design_number: bale.design_number, trade_name: bale.trade_name, quantity: bale.quantity }]
                              : [];
                          
                          const baleTotal = fabricLines.reduce((sum, line) => sum + (parseFloat(line.quantity) || 0), 0);
                          
                          return (
                            <div key={idx} className="bg-maroon-50 rounded p-1 sm:p-2">
                              <div className="flex items-center justify-between text-[10px] sm:text-xs">
                                <span className="font-medium text-maroon-800">
                                  B: {bale.bale_number || '-'} | LR: {bale.lr_number || '-'}
                                </span>
                                <span className="text-maroon-600">
                                  {baleTotal.toFixed(0)}
                                </span>
                              </div>
                              {fabricLines.length > 0 && (
                                <div className="mt-0.5 sm:mt-1 text-[9px] sm:text-xs">
                                  {fabricLines.map((line, lineIdx) => (
                                    <div key={lineIdx} className="flex justify-between text-maroon-700">
                                      <span>{line.design_number || '-'}</span>
                                      <span>{parseFloat(line.quantity || 0).toFixed(0)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {delivery.remarks && (
                    <div className="mt-1.5 sm:mt-3 pt-1.5 sm:pt-3 border-t border-maroon-100">
                      <span className="text-[10px] sm:text-xs text-maroon-600">Remarks</span>
                      <p className="text-[11px] sm:text-sm text-maroon-700">{delivery.remarks}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delivery Dialog - Redesigned for fast desktop & mobile entry */}
      <Dialog open={deliveryDialogOpen} onOpenChange={setDeliveryDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{editingDelivery ? 'Edit Delivery' : 'Add New Delivery'}</span>
              <div className="hidden md:flex items-center gap-1 text-xs text-gray-500">
                <Keyboard className="h-3 w-3" />
                <span>Ctrl+Enter: Save | Alt+B: Add Bale | Alt+L: Add Line</span>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div 
            ref={formRef} 
            className="space-y-5 pt-4" 
            onKeyDown={handleDeliveryFormKeyDown}
            data-testid="delivery-form"
          >
            {/* LR Warning */}
            {lrWarning && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-yellow-800 font-medium">Warning</p>
                  <p className="text-sm text-yellow-700">{lrWarning}</p>
                  <p className="text-xs text-yellow-600 mt-1">Click &quot;Save Anyway&quot; to proceed.</p>
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" variant="outline" onClick={() => setLrWarning(null)}>
                      Cancel
                    </Button>
                    <Button size="sm" className="bg-yellow-600 hover:bg-yellow-700" onClick={() => {
                      setLrWarning(null);
                      handleSaveDelivery(true);
                    }}>
                      Save Anyway
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ========== SECTION 1: DATE FIELDS ========== */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="delivery-date" className="text-sm font-medium">Delivery Date *</Label>
                <div className="relative mt-1">
                  <Input
                    id="delivery-date"
                    type="text"
                    inputMode="numeric"
                    placeholder="DD or DDMM (e.g., 27 or 2706)"
                    value={deliveryForm.delivery_date}
                    onChange={(e) => setDeliveryForm(prev => ({ ...prev, delivery_date: e.target.value }))}
                    onBlur={(e) => handleSmartDateBlur('delivery_date', e.target.value)}
                    className="h-11 md:h-10 text-base md:text-sm pr-10"
                    data-testid="delivery-date-input"
                    tabIndex={1}
                  />
                  <input 
                    type="date" 
                    className="absolute right-0 top-0 h-full w-10 opacity-0 cursor-pointer"
                    value={deliveryForm.delivery_date}
                    onChange={(e) => setDeliveryForm(prev => ({ ...prev, delivery_date: e.target.value }))}
                    tabIndex={-1}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">📅</span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">Type 27 → {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-').replace(/^(\d+)-(\d+)-(\d+)$/, `27-${String(new Date().getMonth() + 1).padStart(2, '0')}-${new Date().getFullYear()}`)}</p>
              </div>
              <div>
                <Label htmlFor="lr-date" className="text-sm font-medium">LR Date</Label>
                <div className="relative mt-1">
                  <Input
                    id="lr-date"
                    type="text"
                    inputMode="numeric"
                    placeholder="DD or DDMM (e.g., 27 or 2706)"
                    value={deliveryForm.lr_date}
                    onChange={(e) => setDeliveryForm(prev => ({ ...prev, lr_date: e.target.value }))}
                    onBlur={(e) => handleSmartDateBlur('lr_date', e.target.value)}
                    className="h-11 md:h-10 text-base md:text-sm pr-10"
                    data-testid="lr-date-input"
                    tabIndex={2}
                  />
                  <input 
                    type="date" 
                    className="absolute right-0 top-0 h-full w-10 opacity-0 cursor-pointer"
                    value={deliveryForm.lr_date}
                    onChange={(e) => setDeliveryForm(prev => ({ ...prev, lr_date: e.target.value }))}
                    tabIndex={-1}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">📅</span>
                </div>
              </div>
            </div>

            {/* ========== SECTION 2: TRANSPORTER ========== */}
            <div>
              <Label htmlFor="transporter" className="text-sm font-medium">Transporter *</Label>
              <MasterDataAutocomplete
                value={deliveryForm.transporter_id}
                onValueChange={(value) => setDeliveryForm(prev => ({ ...prev, transporter_id: value }))}
                options={transporters.map((transporter) => ({
                  value: transporter.id,
                  label: transporter.name,
                  description: [transporter.source, transporter.final_destination].filter(Boolean).join(' → '),
                  keywords: `${transporter.source || ''} ${transporter.final_destination || ''}`,
                }))}
                placeholder="Select transporter..."
                searchPlaceholder="Search transporters..."
                emptyMessage="No matching transporter in Master Data"
                className="mt-1"
                inputClassName="h-11 md:h-10 text-base md:text-sm"
                tabIndex={3}
                testId="transporter-select"
              />
            </div>

            {/* ========== SECTION 3: BALES (MAIN FOCUS) ========== */}
            <div className="bg-cyan-50 border-2 border-cyan-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold text-cyan-800">📦 Bale Entry (Primary Section)</Label>
                <Button 
                  type="button" 
                  size="sm" 
                  variant="outline" 
                  onClick={addBaleRow} 
                  className="h-9 text-sm border-cyan-400 text-cyan-700 hover:bg-cyan-100" 
                  data-testid="add-bale-button"
                  tabIndex={-1}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Bale <span className="hidden md:inline ml-1 text-xs opacity-70">(Alt+B)</span>
                </Button>
              </div>
              
              <div className="space-y-4">
                {deliveryForm.bales.map((bale, baleIndex) => (
                  <div 
                    key={baleIndex} 
                    className="bg-white rounded-lg border border-cyan-200 p-4 shadow-sm" 
                    data-testid={`bale-group-${baleIndex}`}
                  >
                    {/* Bale Header: Bale No, LR No */}
                    <div className="flex flex-col md:flex-row gap-3 mb-4">
                      <div className="flex-1">
                        <Label className="text-sm font-semibold text-cyan-700">Bale No. *</Label>
                        <Input
                          ref={baleIndex === 0 ? firstBaleInputRef : null}
                          placeholder="Enter bale number"
                          value={bale.bale_number}
                          onChange={(e) => updateBaleField(baleIndex, 'bale_number', e.target.value)}
                          className="h-12 md:h-10 text-lg md:text-base font-medium mt-1"
                          data-testid={`bale-number-input-${baleIndex}`}
                          tabIndex={4 + (baleIndex * 10)}
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-sm font-semibold text-cyan-700">LR No. *</Label>
                        <Input
                          placeholder="Enter LR number"
                          value={bale.lr_number}
                          onChange={(e) => updateBaleField(baleIndex, 'lr_number', e.target.value)}
                          className="h-12 md:h-10 text-lg md:text-base font-medium mt-1"
                          data-testid={`lr-number-input-${baleIndex}`}
                          tabIndex={5 + (baleIndex * 10)}
                        />
                      </div>
                      {deliveryForm.bales.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeBaleRow(baleIndex)}
                          className="h-10 w-10 p-0 text-red-600 hover:bg-red-50 self-end"
                          data-testid={`remove-bale-${baleIndex}`}
                          tabIndex={-1}
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      )}
                    </div>
                    
                    {/* Fabric Lines within this bale */}
                    <div className="border-t border-cyan-100 pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium text-gray-700">Design/Color + Quantity</Label>
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => addFabricLine(baleIndex)} 
                          className="h-8 text-sm text-cyan-700 hover:text-cyan-800 hover:bg-cyan-50 px-2"
                          data-testid={`add-fabric-line-button-${baleIndex}`}
                          tabIndex={-1}
                        >
                          <Plus className="h-3 w-3 mr-1" /> Add Line <span className="hidden md:inline ml-1 text-xs opacity-70">(Alt+L)</span>
                        </Button>
                      </div>
                      
                      <div className="space-y-3">
                        {bale.fabric_lines?.map((line, lineIndex) => (
                          <div 
                            key={lineIndex} 
                            className="flex flex-col md:flex-row gap-3 p-3 bg-gray-50 rounded-lg" 
                            data-testid={`fabric-line-${baleIndex}-${lineIndex}`}
                          >
                            <div className="flex-1 min-w-0">
                              <Label className="text-xs text-gray-600 font-medium">Design/Color *</Label>
                              <Select
                                value={line.design_number}
                                onValueChange={(value) => updateFabricLineField(baleIndex, lineIndex, 'design_number', value)}
                              >
                                <SelectTrigger 
                                  className="h-12 md:h-10 mt-1 text-base md:text-sm font-medium" 
                                  data-testid={`fabric-line-design-${baleIndex}-${lineIndex}`}
                                  tabIndex={6 + (baleIndex * 10) + (lineIndex * 2)}
                                >
                                  <SelectValue placeholder="Select design..." />
                                </SelectTrigger>
                                <SelectContent className="bg-white max-h-60">
                                  {availableDesigns.length === 0 ? (
                                    <div className="p-2 text-sm text-maroon-600">No designs in this order</div>
                                  ) : (
                                    getDeliveryDesignOptions({
                                      availableDesigns,
                                      bales: deliveryForm.bales,
                                      baleIndex,
                                      lineIndex,
                                      designSummaries,
                                    }).map((design, idx) => (
                                      <SelectItem key={idx} value={design.design_number}>
                                        <span className="font-medium">{design.design_number}</span>
                                        <span className="text-gray-500 ml-1">({design.trade_name})</span>
                                      </SelectItem>
                                    ))
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="w-full md:w-32">
                              <Label className="text-xs text-gray-600 font-medium">Qty *</Label>
                              <Input
                                type="text"
                                inputMode="decimal"
                                placeholder="Qty"
                                value={line.quantity === 0 || line.quantity === '' || line.quantity === null ? '' : line.quantity}
                                onChange={(e) => updateFabricLineField(baleIndex, lineIndex, 'quantity', e.target.value)}
                                className="h-12 md:h-10 mt-1 text-lg md:text-base font-semibold text-center"
                                data-testid={`fabric-line-qty-${baleIndex}-${lineIndex}`}
                                tabIndex={7 + (baleIndex * 10) + (lineIndex * 2)}
                              />
                            </div>
                            {bale.fabric_lines.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeFabricLine(baleIndex, lineIndex)}
                                className="h-10 w-10 p-0 text-red-500 hover:bg-red-50 self-end md:self-center"
                                data-testid={`remove-fabric-line-${baleIndex}-${lineIndex}`}
                                tabIndex={-1}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-cyan-600 mt-2">
                💡 One bale can contain multiple designs/colors. Press Enter after Qty to add a new line.
              </p>
            </div>

            {/* ========== SECTION 4: INVOICE NUMBERS ========== */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="purchase-invoice" className="text-sm font-medium">Purchase Invoice Number</Label>
                <Input
                  id="purchase-invoice"
                  value={deliveryForm.purchase_invoice_number}
                  onChange={(e) => setDeliveryForm(prev => ({ ...prev, purchase_invoice_number: e.target.value }))}
                  className="mt-1 h-11 md:h-10 text-base md:text-sm"
                  placeholder="Enter purchase invoice"
                  data-testid="purchase-invoice-input"
                  tabIndex={100}
                />
              </div>
              <div>
                <Label htmlFor="sale-invoice" className="text-sm font-medium">Sale Invoice Number</Label>
                <Input
                  id="sale-invoice"
                  value={deliveryForm.sale_invoice_number}
                  onChange={(e) => setDeliveryForm(prev => ({ ...prev, sale_invoice_number: e.target.value }))}
                  className="mt-1 h-11 md:h-10 text-base md:text-sm"
                  placeholder="Enter sale invoice"
                  data-testid="sale-invoice-input"
                  tabIndex={101}
                />
              </div>
            </div>

            {/* ========== SECTION 5: STATUS (MOVED TO BOTTOM) ========== */}
            <div>
              <Label htmlFor="status" className="text-sm font-medium">Delivery Status</Label>
              <Select
                value={deliveryForm.status}
                onValueChange={(value) => setDeliveryForm(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger className="mt-1 h-11 md:h-10 text-base md:text-sm" tabIndex={102} data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Partially Delivered">Partially Delivered</SelectItem>
                  <SelectItem value="Delivered">Delivered</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-0.5">Default: Pending. Change only if needed.</p>
            </div>

            {/* CANCELLED STATUS - Simplified Form (shown when Cancelled is selected) */}
            {deliveryForm.status === 'Cancelled' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700 font-medium mb-3">⚠️ Recording a cancellation for this delivery</p>
                
                {/* Design Selection for Cancellation */}
                <div className="mb-3">
                  <Label className="text-sm text-red-700 font-medium">Design *</Label>
                  <Select
                    value={deliveryForm.bales[0]?.fabric_lines?.[0]?.design_number || ''}
                    onValueChange={(value) => {
                      const design = availableDesigns.find(d => d.design_number === value);
                      setDeliveryForm(prev => ({
                        ...prev,
                        bales: [{
                          ...prev.bales[0],
                          fabric_lines: [{
                            design_number: value,
                            trade_name: design?.trade_name || '',
                            quantity: 0,
                            unit: 'MTRS'
                          }]
                        }]
                      }));
                    }}
                  >
                    <SelectTrigger className="mt-1 bg-white h-11 md:h-10 text-base md:text-sm">
                      <SelectValue placeholder="Select design to cancel..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-60">
                      {availableDesigns.length === 0 ? (
                        <div className="p-2 text-sm text-maroon-600">No designs in this order</div>
                      ) : (
                        availableDesigns.map((design, idx) => (
                          <SelectItem key={idx} value={design.design_number}>
                            {design.design_number} ({design.trade_name})
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Remarks for Cancellation - REQUIRED */}
                <div>
                  <Label className="text-sm text-red-700 font-medium">Remarks * (reason for cancellation)</Label>
                  <Input
                    value={deliveryForm.remarks}
                    onChange={(e) => setDeliveryForm(prev => ({ ...prev, remarks: e.target.value }))}
                    className="mt-1 bg-white h-11 md:h-10 text-base md:text-sm"
                    placeholder="Enter reason for cancellation..."
                  />
                </div>
              </div>
            )}

            {/* ========== SECTION 6: REMARKS (for non-cancelled) ========== */}
            {deliveryForm.status !== 'Cancelled' && (
              <div>
                <Label htmlFor="remarks" className="text-sm font-medium">Remarks</Label>
                <Input
                  id="remarks"
                  value={deliveryForm.remarks}
                  onChange={(e) => setDeliveryForm(prev => ({ ...prev, remarks: e.target.value }))}
                  className="mt-1 h-11 md:h-10 text-base md:text-sm"
                  placeholder="Any additional notes (optional)"
                  data-testid="remarks-input"
                  tabIndex={103}
                />
              </div>
            )}

            {/* ========== SECTION 7: SAVE BUTTON ========== */}
            <Button
              onClick={() => handleSaveDelivery(false)}
              disabled={submitting || !!lrWarning}
              className={`w-full h-12 md:h-11 text-base md:text-sm font-semibold ${
                deliveryForm.status === 'Cancelled' 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-cyan-600 hover:bg-cyan-700'
              }`}
              data-testid="save-delivery-button"
              tabIndex={104}
            >
              {submitting ? 'Saving...' : (
                deliveryForm.status === 'Cancelled' 
                  ? (editingDelivery ? 'Update Cancellation' : 'Save Cancellation')
                  : (editingDelivery ? 'Update Delivery' : 'Save Delivery')
              )}
              <span className="hidden md:inline ml-2 text-xs opacity-80">(Ctrl+Enter)</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={auditDialogOpen} onOpenChange={setAuditDialogOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Audit Trail - {order.order_details.indent_number}
            </DialogTitle>
          </DialogHeader>
          <div className="pt-4">
            {auditLogs.length === 0 ? (
              <div className="text-center py-8 text-maroon-600">
                <History className="h-12 w-12 mx-auto mb-3 text-maroon-400" />
                <p>No audit records found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {auditLogs.map((log) => (
                  <div key={log.id} className="border border-maroon-200 rounded-lg p-3 text-sm">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          log.action === 'CREATE' ? 'bg-green-100 text-green-800' :
                          log.action === 'UPDATE' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {log.action}
                        </span>
                        <span className="ml-2 px-2 py-0.5 rounded text-xs bg-maroon-100 text-maroon-700">
                          {log.entity_type}
                        </span>
                      </div>
                      <span className="text-xs text-maroon-500">
                        {new Date(log.timestamp).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <p className="text-maroon-950">{log.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-maroon-600">
                      <span>User: {log.user_name}</span>
                      {log.field_changed && (
                        <span>Field: {log.field_changed}</span>
                      )}
                    </div>
                    {(log.old_value || log.new_value) && (
                      <div className="mt-2 p-2 bg-maroon-50 rounded text-xs">
                        {log.old_value && <p><span className="font-medium">Old:</span> {log.old_value}</p>}
                        {log.new_value && <p><span className="font-medium">New:</span> {log.new_value}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Export Section */}
      <Card className="border-maroon-200 border-l-4 border-l-violet-500">
        <CardHeader className="bg-violet-50/50">
          <CardTitle className="text-lg lg:text-xl font-semibold text-violet-900">Export</CardTitle>
        </CardHeader>
        <CardContent className="pt-4 lg:pt-3">
          <div className="flex flex-wrap gap-3">
            <Button
              data-testid="export-button"
              variant="outline"
              onClick={() => setExportDialogOpen(true)}
              className="border-maroon-300 text-maroon-700 hover:bg-maroon-50"
              title="Export (Alt+E)"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Order
            </Button>
            <Button
              data-testid="export-buyer-pdf-button"
              onClick={handleExportBuyerPDF}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <User className="h-4 w-4 mr-2" />
              Buyer PDF
            </Button>
            <Button
              data-testid="export-supplier-pdf-button"
              onClick={handleExportSupplierPDF}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Building2 className="h-4 w-4 mr-2" />
              Supplier PDF
            </Button>
          </div>
          <p className="text-xs text-maroon-600 mt-3">
            Export Order: Full data in Excel/PDF. Buyer PDF: sale rates. Supplier PDF: purchase rates.
          </p>
        </CardContent>
      </Card>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        onExport={handleExport}
        title={`Export Order ${order?.order_details?.indent_number || ''}`}
        hasFilters={false}
        filterDescription=""
        totalRecords={1}
        filteredRecords={1}
      />
    </div>
  );
}
